import { STANDARD_EVENTS } from "../types/Amplitude";
import {
  NON_USER_EVENTS,
  USER_EVENTS,
  PAGEVIEW_EVENTS,
  VIDEO_EVENTS,
  ECOMMERCE_EVENTS,
  INTERACTION_EVENTS,
} from "../types/Gtm";
import { formatTrackingNameValue, stripUnderscoresFromObject } from "./general";
import { constructCtaProductData } from "./shop";

// Custom functions for gtm tracking
const getFormattedTitle = (title) => {
  if (title === "Default Title") return "";
  if (Number(title) > 0) return "";
  return title;
};
const getFormattedPrice = (price) => Number(price).toFixed(2);
const getProductCategory = (handle) => {
  if (["restore", "restore-barrel-power-cord"].includes(handle)) {
    return "Adult Sleep";
  } else if (
    [
      "rest",
      "rest-plus",
      "rest-mini",
      "rest-2nd-gen",
      "coverlets",
      "rest-power-cord-usb",
      "rest-rest-plus-barrel-power-cord",
      "hatch-cloud-pillow",
      "rest-travel-case",
      "baby-milestone-cards",
    ].includes(handle)
  ) {
    return "Babies & Kids Sleep";
  } else {
    return "Everyone";
  }
};

// For creating data layers for Google Tag Manager
export const googleAnalyticsStandardEventTracking =
  () => (next) => (action) => {
    // If event is being tracked by GTM, then add dataLayer
    if (NON_USER_EVENTS.indexOf(action.type) > -1) {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: formatTrackingNameValue(action.type),
        data: stripUnderscoresFromObject(action.data),
      });
    } else if (PAGEVIEW_EVENTS.indexOf(action.type) > -1) {
      window.dataLayer = window.dataLayer || [];
      const actionType = action.type;
      const actionPayload = action.data;

      if (actionType === STANDARD_EVENTS.Page_View) {
        const { title, type } = actionPayload;
        let environment = process.env.NODE_ENV;
        if (
          environment === "production" &&
          document.location.origin.includes("qa.hatch.co")
        ) {
          environment = "qa";
        }

        window.dataLayer.push({
          event: "pageView",
          event_name: "page_view",
          page: {
            title: title,
            type: type,
            environment: environment,
          },
        });
      }
    } else if (VIDEO_EVENTS.indexOf(action.type) > -1) {
      window.dataLayer = window.dataLayer || [];
      const actionType = action.type;
      const actionPayload = action.data;

      if (actionType === STANDARD_EVENTS.Video_Start) {
        const { video_duration, video_provider, video_title, video_url } =
          actionPayload;

        window.dataLayer.push({
          event: "interactionEvent",
          event_name: "video_start",
          events: {
            video_duration: video_duration,
            video_provider: video_provider,
            video_title: video_title,
            video_url: video_url,
          },
        });
      }
      if (actionType === STANDARD_EVENTS.Video_Progress) {
        const { video_duration, video_provider, video_title, video_url } =
          actionPayload;

        window.dataLayer.push({
          event: "interactionEvent",
          event_name: "video_progress",
          events: {
            video_duration: video_duration,
            video_provider: video_provider,
            video_title: video_title,
            video_url: video_url,
          },
        });
      }
      if (actionType === STANDARD_EVENTS.Video_Complete) {
        const { video_duration, video_provider, video_title, video_url } =
          actionPayload;

        window.dataLayer.push({
          event: "interactionEvent",
          event_name: "video_complete",
          events: {
            video_duration: video_duration,
            video_provider: video_provider,
            video_title: video_title,
            video_url: video_url,
          },
        });
      }
    } else if (ECOMMERCE_EVENTS.indexOf(action.type) > -1) {
      window.dataLayer = window.dataLayer || [];
      const actionType = action.type;
      const actionPayload = action.data;

      if (actionType === STANDARD_EVENTS.View_Item_List) {
        const { itemListName, productList } = actionPayload;

        const items = productList.map((item, index) => {
          const { variantData } = constructCtaProductData(item);
          return {
            item_id: variantData.sku,
            item_name: item.productName,
            item_list_name: itemListName,
            currency: "USD",
            item_brand: "Hatch",
            item_category: getProductCategory(item.handle),
            item_variant: getFormattedTitle(variantData.title),
            price: getFormattedPrice(variantData.price),
            index: index + 1,
          };
        });

        const products = productList.map((item, index) => {
          const { variantData } = constructCtaProductData(item);
          return {
            name: item.productName,
            id: variantData.sku,
            list: itemListName,
            price: getFormattedPrice(variantData.price),
            brand: "Hatch",
            category: getProductCategory(item.handle),
            variant: getFormattedTitle(variantData.title),
            position: index + 1,
          };
        });

        window.dataLayer.push({
          event: "ecommEvent",
          event_name: "view_item_list",
          ecommerce: {
            currencyCode: "USD",
            item_list_name: itemListName,
            items: items,
            impressions: products,
          },
        });
      } else if (actionType === STANDARD_EVENTS.Select_Item) {
        const { itemListName, product } = actionPayload;
        const { variantData } = constructCtaProductData(product);

        window.dataLayer.push({
          event: "ecommEvent",
          event_name: "select_item",
          ecommerce: {
            currencyCode: "USD",
            item_list_name: itemListName,
            items: [
              {
                currency: "USD",
                item_id: variantData.sku,
                item_name: product.productName,
                item_brand: "Hatch",
                item_category: getProductCategory(product.handle),
                item_variant: getFormattedTitle(variantData.title),
                price: getFormattedPrice(variantData.price),
                quantity: 1,
              },
            ],
            click: {
              actionField: {
                list: itemListName,
              },
              products: [
                {
                  id: variantData.sku,
                  name: product.productName,
                  price: getFormattedPrice(variantData.price),
                  brand: "Hatch",
                  category: getProductCategory(product.handle),
                  variant: getFormattedTitle(variantData.title),
                  quantity: 1,
                },
              ],
            },
          },
        });
      } else if (actionType === STANDARD_EVENTS.View_Item) {
        const { product, variantId } = actionPayload;
        const { variantData } = constructCtaProductData(product, variantId);

        window.dataLayer.push({
          event: "ecommEvent",
          event_name: "view_item",
          ecommerce: {
            currencyCode: "USD",
            items: [
              {
                currency: "USD",
                item_id: variantData.sku,
                item_name: product.title,
                item_brand: "Hatch",
                item_category: getProductCategory(product.handle),
                item_variant: getFormattedTitle(variantData.title),
                price: getFormattedPrice(variantData.price),
                quantity: 1,
              },
            ],
            detail: {
              products: [
                {
                  id: variantData.sku,
                  name: product.title,
                  price: getFormattedPrice(variantData.price),
                  brand: "Hatch",
                  category: getProductCategory(product.handle),
                  variant: getFormattedTitle(variantData.title),
                  quantity: 1,
                },
              ],
            },
          },
        });
      } else if (actionType === STANDARD_EVENTS.Add_To_Shopify_Cart) {
        const { productShopifyData, variantData, lineItem } = actionPayload;

        if (lineItem) {
          window.dataLayer.push({
            event: "ecommEvent",
            event_name: "add_to_cart",
            ecommerce: {
              currencyCode: "USD",
              items: [
                {
                  currency: "USD",
                  item_id: lineItem.sku,
                  item_name: lineItem.productName,
                  item_brand: "Hatch",
                  item_category: getProductCategory(lineItem.handle),
                  item_variant: getFormattedTitle(lineItem.variantTitle),
                  price: getFormattedPrice(lineItem.price),
                  quantity: 1,
                },
              ],
              add: {
                products: [
                  {
                    id: lineItem.sku,
                    name: lineItem.productName,
                    price: getFormattedPrice(lineItem.price),
                    brand: "Hatch",
                    category: getProductCategory(lineItem.handle),
                    variant: getFormattedTitle(lineItem.variantTitle),
                    quantity: 1,
                  },
                ],
              },
            },
          });
        } else {
          window.dataLayer.push({
            event: "ecommEvent",
            event_name: "add_to_cart",
            ecommerce: {
              currencyCode: "USD",
              items: [
                {
                  currency: "USD",
                  item_id: variantData.sku,
                  item_name: productShopifyData.title,
                  item_brand: "Hatch",
                  item_category: getProductCategory(productShopifyData.handle),
                  item_variant: getFormattedTitle(variantData.title),
                  price: getFormattedPrice(variantData.price),
                  quantity: 1,
                },
              ],
              add: {
                products: [
                  {
                    id: variantData.sku,
                    name: productShopifyData.title,
                    price: getFormattedPrice(variantData.price),
                    brand: "Hatch",
                    category: getProductCategory(productShopifyData.handle),
                    variant: getFormattedTitle(variantData.title),
                    quantity: 1,
                  },
                ],
              },
            },
          });
        }
      } else if (actionType === STANDARD_EVENTS.Remove_From_Cart) {
        const { currentQuantity, lineItem } = actionPayload;

        window.dataLayer.push({
          event: "ecommEvent",
          event_name: "remove_from_cart",
          ecommerce: {
            currencyCode: "USD",
            items: [
              {
                currency: "USD",
                item_id: lineItem.sku,
                item_name: lineItem.productName,
                item_brand: "Hatch",
                item_category: getProductCategory(lineItem.handle),
                item_variant: getFormattedTitle(lineItem.variantTitle),
                price: getFormattedPrice(lineItem.price),
                quantity: currentQuantity,
              },
            ],
            remove: {
              products: [
                {
                  id: lineItem.sku,
                  name: lineItem.productName,
                  price: getFormattedPrice(lineItem.price),
                  brand: "Hatch",
                  category: getProductCategory(lineItem.handle),
                  variant: getFormattedTitle(lineItem.variantTitle),
                  quantity: currentQuantity,
                },
              ],
            },
          },
        });
      } else if (actionType === STANDARD_EVENTS.View_Cart) {
        let totalPrice = 0;

        actionPayload.forEach((item) => {
          totalPrice += item.variant.price * item.quantity;
        });

        const items = actionPayload.map((item) => ({
          item_id: item.variant.sku,
          item_name: item.title,
          currency: "USD",
          item_brand: "Hatch",
          item_category: getProductCategory(item.variant.product.handle),
          item_variant: getFormattedTitle(item.variant.title),
          price: getFormattedPrice(item.variant.price),
          quantity: item.quantity,
        }));

        window.dataLayer.push({
          event: "ecommEvent",
          event_name: "view_cart",
          ecommerce: {
            value: getFormattedPrice(totalPrice),
            currencyCode: "USD",
            items,
          },
        });
      } else if (actionType === STANDARD_EVENTS.Checkout) {
        const { lineItems, subtotal } = actionPayload;

        const items = lineItems.map((item) => ({
          item_id: item.variant.sku,
          item_name: item.title,
          currency: "USD",
          item_brand: "Hatch",
          item_category: getProductCategory(item.variant.product.handle),
          item_variant: getFormattedTitle(item.variant.title),
          price: getFormattedPrice(item.variant.price),
          quantity: item.quantity,
        }));

        const products = lineItems.map((item) => ({
          name: item.title,
          id: item.variant.sku,
          price: getFormattedPrice(item.variant.price),
          brand: "Hatch",
          category: getProductCategory(item.variant.product.handle),
          variant: getFormattedTitle(item.variant.title),
          quantity: item.quantity,
        }));

        window.dataLayer.push({
          event: "ecommEvent",
          event_name: "begin_checkout",
          ecommerce: {
            value: getFormattedPrice(subtotal),
            currencyCode: "USD",
            items,
            checkout: {
              actionField: { step: 1 },
              products,
            },
          },
        });
      }
    } else if (INTERACTION_EVENTS.indexOf(action.type) > -1) {
      window.dataLayer = window.dataLayer || [];
      const actionType = action.type;
      const actionPayload = action.data;

      if (actionType === STANDARD_EVENTS.Social_Share) {
        const { event } = actionPayload;

        window.dataLayer.push({
          event: "interactionEvent",
          event_name: "social_share",
          events: {
            social: event,
          },
        });
      } else if (actionType === STANDARD_EVENTS.App_Download) {
        const { app_platform } = actionPayload;

        window.dataLayer.push({
          event: "interactionEvent",
          event_name: "app_download",
          events: {
            app_platform: app_platform,
          },
        });
      } else if (actionType === STANDARD_EVENTS.File_Download) {
        const { fileExtension, fileName } = actionPayload;

        window.dataLayer.push({
          event: "interactionEvent",
          event_name: "file_download",
          events: {
            file_extension: fileExtension,
            file_name: fileName,
          },
        });
      } else if (actionType === STANDARD_EVENTS.Generate_Lead) {
        const { company_name } = actionPayload;

        window.dataLayer.push({
          event: "interactionEvent",
          event_name: "generate_lead",
          events: {
            lead_type: "hatch for work",
            company_name: company_name,
          },
        });
      } else if (actionType === STANDARD_EVENTS.Email_Signup) {
        window.dataLayer.push({
          event: "interactionEvent",
          event_name: "email_signup",
          events: {
            lead_type: "email signup",
            location: "footer",
          },
        });
      } else if (actionType === STANDARD_EVENTS.Audio_Start) {
        const { audioTitle } = actionPayload;

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: "interactionEvent",
          event_name: "audio_start",
          events: {
            audio_title: audioTitle,
          },
        });
      } else if (actionType === STANDARD_EVENTS.Faq_Action) {
        const { product, isOpen, title } = actionPayload;
        const { variantData } = constructCtaProductData(product);
        const faqAction = isOpen ? "collapse" : "expand";
        const faqTitle = title;

        window.dataLayer.push({
          event: "interactionEvent",
          event_name: "faq",
          events: {
            page_type: "product",
            faq_title: faqTitle,
            faq_action: faqAction,
          },
          ecommerce: {
            items: [
              {
                currency: "USD",
                item_id: variantData.sku,
                item_name: product.title,
                item_brand: "Hatch",
                item_category: getProductCategory(product.handle),
                item_variant: getFormattedTitle(variantData.title),
                price: getFormattedPrice(variantData.price),
                quantity: 1,
              },
            ],
          },
        });
      } else if (actionType === STANDARD_EVENTS.Faq_Load_More) {
        const { variantData } = constructCtaProductData(actionPayload);

        window.dataLayer.push({
          event: "interactionEvent",
          event_name: "faq_load_more",
          events: {
            click_text: "Load More",
          },
          ecommerce: {
            items: [
              {
                currency: "USD",
                item_id: variantData.sku,
                item_name: actionPayload.title,
                item_brand: "Hatch",
                item_category: getProductCategory(actionPayload.handle),
                item_variant: getFormattedTitle(variantData.title),
                price: getFormattedPrice(variantData.price),
                quantity: 1,
              },
            ],
          },
        });
      } else if (actionType === STANDARD_EVENTS.Reviews_Cta) {
        const { product, clickText, reviewsRating } = actionPayload;
        const { variantData } = constructCtaProductData(product);

        window.dataLayer.push({
          event: "interactionEvent",
          event_name: "reviews_cta",
          events: {
            click_text: clickText,
            reviews_rating: reviewsRating,
          },
          ecommerce: {
            items: [
              {
                currency: "USD",
                item_id: variantData.sku,
                item_name: product.title,
                item_brand: "Hatch",
                item_category: getProductCategory(product.handle),
                item_variant: getFormattedTitle(variantData.title),
                price: getFormattedPrice(variantData.price),
                quantity: 1,
              },
            ],
          },
        });
      } else if (actionType === STANDARD_EVENTS.Navigation) {
        const { clickURL, clickText, location } = actionPayload;

        window.dataLayer.push({
          event: "interactionEvent",
          event_name: "navigation",
          events: {
            click_text: clickText,
            click_url: clickURL,
            location: location,
          },
        });
      } else if (actionType === STANDARD_EVENTS.Cta_Click) {
        const { clickURL, clickText, ctaType, location } = actionPayload;

        window.dataLayer.push({
          event: "interactionEvent",
          event_name: "cta_click",
          events: {
            click_text: clickText,
            click_url: clickURL,
            cta_type: ctaType,
            location: location,
          },
        });
      } else if (actionType === STANDARD_EVENTS.Item_Details) {
        const { productData, clickText } = actionPayload;
        const { variantData } = constructCtaProductData(productData);

        window.dataLayer.push({
          event: "interactionEvent",
          event_name: "item_details",
          events: {
            click_text: clickText,
            cta_type: "menu",
          },
          ecommerce: {
            items: [
              {
                currency: "USD",
                item_id: variantData.sku,
                item_name: productData.title,
                item_brand: "Hatch",
                item_category: getProductCategory(productData.handle),
                item_variant: getFormattedTitle(variantData.title),
                price: getFormattedPrice(variantData.price),
                quantity: 1,
              },
            ],
          },
        });
      }
    }

    return next(action);
  };

const googleAnalyticsUserTracking = () => (next) => (action) => {
  if (USER_EVENTS.indexOf(action.type) > -1) {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: formatTrackingNameValue(action.type),
      user: action.user || { id: null },
    });
  }

  return next(action);
};

const middleware = [
  googleAnalyticsStandardEventTracking,
  googleAnalyticsUserTracking,
];

export default middleware;
